import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BadgeCheck,
  Check,
  Copy,
  FileDown,
  Gift,
  Loader2,
  Lock,
  Mail,
  MessageCircle,
  Printer,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { StorybookShell } from '@/components/storybook/StorybookShell';
import { SectionHeading } from '@/components/storybook/marketing/SectionHeading';
import { trackEvent, trackPageView } from '@/lib/analytics';
import {
  StorybookApiError,
  createStorybookGift,
  getStorybookConfig,
  lookupStorybookGift,
  readStorybookGiftCode,
  saveStorybookGiftCode,
  storybookGiftLink,
  type StorybookConfig,
  type StorybookGift as Gifted,
  type StorybookLanguage,
  type StorybookProduct,
} from '@/lib/storybook-api';
import { formatCountryList, formatStorybookPrice, storybookTotalWithShipping, storybookUiLanguage } from '@/lib/storybook-utils';
import { cn } from '@/lib/utils';

const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,190}\.[^\s@]{2,24}$/;
const NAME_MAX = 40;
const CHILD_MAX = 24;
const MESSAGE_MAX = 300;
const STEPS = [
  { key: 'pay', icon: Gift },
  { key: 'upload', icon: Upload },
  { key: 'approve', icon: BadgeCheck },
] as const;

type Field = 'fromName' | 'fromEmail' | 'toName' | 'toEmail' | 'childName' | 'message';
type Form = Record<Field, string> & { product: StorybookProduct; language: StorybookLanguage };

/**
 * Gift a storybook (owner blueprint, 2026-10-16): the giver pays here, the
 * parent gets an email with a private link (/storybook/gift/redeem) to upload
 * photos and approve the pictures. After Stripe, ?checkout=success shows the
 * link to share by hand too.
 */
export default function StorybookGift() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const giftId = params.get('gift') || '';
  const checkout = params.get('checkout');
  const [config, setConfig] = useState<StorybookConfig | null>(null);
  const [configState, setConfigState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    trackPageView('storybook_gift');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setConfigState('loading');
    getStorybookConfig(controller.signal)
      .then((next) => {
        setConfig(next);
        setConfigState('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setConfigState('error');
      });
    return () => controller.abort();
  }, [reloadKey]);

  const sent = checkout === 'success' && /^[a-f0-9]{24}$/.test(giftId);

  return (
    <StorybookShell title={`${t('storybookGift.meta.title')} | FaceShot-ChopShop`}>
      <section className="relative overflow-hidden" aria-labelledby="gift-title">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[hsl(330_85%_88%)] opacity-60 blur-3xl" />
          <div className="absolute -right-16 top-10 h-72 w-72 rounded-full bg-[hsl(45_95%_78%)] opacity-60 blur-3xl" />
        </div>
        <div className="container relative mx-auto px-4 pb-6 pt-8 text-center sm:pt-14">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-4 py-1.5 text-sm font-semibold text-primary shadow-sm">
            <Gift className="h-4 w-4" aria-hidden="true" />
            {t('storybookGift.hero.eyebrow')}
          </span>
          <h1 id="gift-title" className="mx-auto mt-5 max-w-3xl text-4xl font-semibold leading-[1.1] [text-wrap:balance] sm:text-5xl">
            {t('storybookGift.hero.title')}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">{t('storybookGift.hero.subtitle')}</p>
          <ol className="mx-auto mt-8 grid max-w-4xl gap-3 text-left sm:grid-cols-3">
            {STEPS.map((step, index) => (
              <li key={step.key} className="flex gap-3 rounded-2xl bg-card/90 p-4 shadow-[var(--shadow-card)]">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-secondary text-secondary-foreground">
                  <step.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-primary">{t('storybookGift.steps.n', { n: index + 1 })}</span>
                  <span className="block font-semibold">{t(`storybookGift.steps.${step.key}.title`)}</span>
                  <span className="block text-sm text-muted-foreground">{t(`storybookGift.steps.${step.key}.body`)}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="pb-20 pt-6" aria-label={t('storybookGift.form.title')}>
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl">
            {configState === 'loading' && (
              <div role="status" className="glass-card flex items-center justify-center gap-3 p-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
                {t('storybookPage.builder.loadingConfig')}
              </div>
            )}
            {configState === 'error' && (
              <div role="alert" className="glass-card p-8 text-center">
                <p className="font-semibold">{t('storybookPage.builder.configError')}</p>
                <Button variant="outline" className="mt-5 rounded-full" onClick={() => setReloadKey((k) => k + 1)}>
                  <RefreshCw aria-hidden="true" />
                  {t('storybookPage.builder.retry')}
                </Button>
              </div>
            )}
            {configState === 'ready' && config && !config.enabled && (
              <div className="glass-card p-8 text-center">
                <h2 className="text-2xl font-semibold">{t('storybookPage.launching.title')}</h2>
                <p className="mt-3 text-muted-foreground">{t('storybookPage.launching.body')}</p>
              </div>
            )}
            {configState === 'ready' && config?.enabled && (sent
              ? <GiftSent giftId={giftId} />
              : <GiftForm config={config} cancelled={checkout === 'cancelled'} uiLanguage={storybookUiLanguage(i18n.language)} />)}
          </div>
        </div>
      </section>
    </StorybookShell>
  );
}

function GiftForm({ config, cancelled, uiLanguage }: { config: StorybookConfig; cancelled: boolean; uiLanguage: StorybookLanguage }) {
  const { t, i18n } = useTranslation();
  const printEnabled = config.printEnabled === true;
  const [form, setForm] = useState<Form>({
    product: printEnabled ? 'print' : 'pdf',
    language: uiLanguage,
    fromName: '',
    fromEmail: '',
    toName: '',
    toEmail: '',
    childName: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const pdf = formatStorybookPrice(config.prices.pdf);
  const printTotal = storybookTotalWithShipping(config.prices.print, config.prices.shipping) || formatStorybookPrice(config.prices.print);
  const countries = formatCountryList(config.printCountries || [], i18n.language);
  const total = form.product === 'print' ? printTotal : pdf;

  const update = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const e: Partial<Record<Field, string>> = {};
    if (!form.fromName.trim()) e.fromName = t('storybookGift.validation.name');
    if (!EMAIL_RE.test(form.fromEmail.trim())) e.fromEmail = t('storybookGift.validation.email');
    if (!form.toName.trim()) e.toName = t('storybookGift.validation.name');
    if (!EMAIL_RE.test(form.toEmail.trim())) e.toEmail = t('storybookGift.validation.email');
    if (form.childName.trim() && !/^[\p{L}\p{M}][\p{L}\p{M}' .-]{0,23}$/u.test(form.childName.trim())) e.childName = t('storybookGift.validation.childName');
    setErrors(e);
    const first = Object.keys(e)[0];
    if (first) document.getElementById(`gift-${first}`)?.focus();
    return !first;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!validate()) return;
    setSubmitting(true);
    trackEvent('storybook_gift_checkout_started', { product: form.product, language: form.language });
    try {
      const created = await createStorybookGift({
        product: form.product,
        language: form.language,
        fromName: form.fromName.trim(),
        fromEmail: form.fromEmail.trim(),
        toName: form.toName.trim(),
        toEmail: form.toEmail.trim(),
        childName: form.childName.trim(),
        message: form.message.trim(),
      });
      saveStorybookGiftCode(created.id, created.code);
      window.location.assign(created.url);
    } catch (err) {
      const code = err instanceof StorybookApiError ? err.code : 'generic';
      if (code === 'invalid_input' && err instanceof StorybookApiError && err.field) {
        setErrors((prev) => ({ ...prev, [err.field as Field]: t('storybookPage.errors.invalidField') }));
      }
      setFormError(t(`storybookGift.errors.${['print_unavailable', 'payments_unavailable', 'network_error'].includes(code) ? code : 'generic'}`));
      setSubmitting(false);
    }
  };

  const field = (name: Field, opts: { type?: string; max: number; autoComplete?: string; optional?: boolean; area?: boolean }) => {
    const id = `gift-${name}`;
    const err = errors[name];
    const common = {
      id,
      value: form[name],
      maxLength: opts.max,
      'aria-invalid': err ? true : undefined,
      'aria-describedby': err ? `${id}-error` : `${id}-help`,
      placeholder: t(`storybookGift.form.${name}.placeholder`),
    };
    return (
      <div>
        <label htmlFor={id} className="flex items-baseline justify-between gap-2 text-sm font-semibold">
          {t(`storybookGift.form.${name}.label`)}
          {opts.optional && <span className="text-xs font-normal text-muted-foreground">{t('storybookGift.form.optional')}</span>}
        </label>
        {opts.area ? (
          <Textarea {...common} rows={4} onChange={(e) => update(name, e.target.value)} className={cn('mt-1.5 text-base', err && 'border-destructive')} />
        ) : (
          <Input
            {...common}
            type={opts.type || 'text'}
            inputMode={opts.type === 'email' ? 'email' : undefined}
            autoComplete={opts.autoComplete}
            onChange={(e) => update(name, e.target.value)}
            className={cn('mt-1.5 h-11 text-base', err && 'border-destructive')}
          />
        )}
        {err ? (
          <p id={`${id}-error`} className="mt-1 text-sm font-medium text-destructive">{err}</p>
        ) : (
          <p id={`${id}-help`} className="mt-1 text-xs text-muted-foreground">
            {opts.area ? t('storybookGift.form.message.help', { left: MESSAGE_MAX - form.message.length }) : t(`storybookGift.form.${name}.help`)}
          </p>
        )}
      </div>
    );
  };

  const products: { id: StorybookProduct; icon: typeof FileDown; price: { mxn: string; usd: string } }[] = [
    ...(printEnabled ? [{ id: 'print' as const, icon: Printer, price: printTotal }] : []),
    { id: 'pdf', icon: FileDown, price: pdf },
  ];

  return (
    <form onSubmit={submit} noValidate className="glass-card space-y-8 p-5 sm:p-8" data-testid="storybook-gift-form">
      <SectionHeading eyebrow={t('storybookGift.form.eyebrow')} title={t('storybookGift.form.title')} className="mb-0" />
      {cancelled && (
        <p role="status" className="rounded-2xl bg-muted px-4 py-3 text-sm font-medium">{t('storybookGift.form.cancelled')}</p>
      )}

      <fieldset>
        <legend className="text-sm font-semibold">{t('storybookGift.form.product.label')}</legend>
        <div role="radiogroup" className={cn('mt-2 grid gap-3', products.length > 1 && 'sm:grid-cols-2')}>
          {products.map((p) => {
            const on = form.product === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => update('product', p.id)}
                data-testid={`gift-product-${p.id}`}
                className={cn(
                  'flex min-h-[44px] flex-col gap-2 rounded-2xl border-2 bg-card p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  on ? 'border-primary shadow-[var(--shadow-glow)]' : 'border-border hover:border-primary/50',
                )}
              >
                <span className="flex items-center gap-3">
                  <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl', on ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground')}>
                    <p.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-semibold leading-tight">{t(`storybookGift.form.product.${p.id}.name`)}</span>
                  {on && <Check className="ml-auto h-5 w-5 text-primary" aria-hidden="true" />}
                </span>
                <span className="text-sm text-muted-foreground">
                  {p.id === 'print' ? t('storybookGift.form.product.print.desc', { countries }) : t('storybookGift.form.product.pdf.desc')}
                </span>
                <span className="text-lg font-extrabold">
                  {p.price.mxn} <span className="text-xs font-bold text-muted-foreground">MXN</span>{' '}
                  <span className="text-sm font-semibold text-muted-foreground">· {p.price.usd} USD</span>
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="mb-1 text-base font-semibold">{t('storybookGift.form.fromLegend')}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {field('fromName', { max: NAME_MAX, autoComplete: 'name' })}
          {field('fromEmail', { type: 'email', max: 254, autoComplete: 'email' })}
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="mb-1 text-base font-semibold">{t('storybookGift.form.toLegend')}</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {field('toName', { max: NAME_MAX, autoComplete: 'off' })}
          {field('toEmail', { type: 'email', max: 254, autoComplete: 'off' })}
        </div>
        {field('childName', { max: CHILD_MAX, autoComplete: 'off', optional: true })}
        {field('message', { max: MESSAGE_MAX, optional: true, area: true })}
        <div>
          <span id="gift-language-label" className="text-sm font-semibold">{t('storybookGift.form.language.label')}</span>
          <div role="radiogroup" aria-labelledby="gift-language-label" className="mt-2 flex flex-wrap gap-2">
            {(['en', 'es'] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                role="radio"
                aria-checked={form.language === lang}
                onClick={() => update('language', lang)}
                className={cn(
                  'min-h-[44px] rounded-full border px-5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  form.language === lang ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:border-primary/50',
                )}
              >
                {t(`storybookGift.form.language.${lang}`)}
              </button>
            ))}
          </div>
        </div>
      </fieldset>

      {formError && <p role="alert" className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">{formError}</p>}

      <div className="space-y-3">
        <Button type="submit" size="lg" disabled={submitting} className="h-auto min-h-12 w-full whitespace-normal rounded-full py-3 text-base" data-testid="gift-pay">
          {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Gift aria-hidden="true" />}
          {t('storybookGift.form.pay', { mxn: total.mxn, usd: total.usd })}
        </Button>
        <p className="flex items-start justify-center gap-1.5 text-center text-xs text-muted-foreground">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t(form.product === 'print' ? 'storybookGift.form.payNotePrint' : 'storybookGift.form.payNote')}
        </p>
      </div>
    </form>
  );
}

function GiftSent({ giftId }: { giftId: string }) {
  const { t } = useTranslation();
  const [code] = useState(() => readStorybookGiftCode(giftId));
  const [gift, setGift] = useState<Gifted | null>(null);
  const [copied, setCopied] = useState(false);
  const [tries, setTries] = useState(0);

  // The webhook may land a few seconds after Stripe sends the giver back.
  useEffect(() => {
    if (!code) return undefined;
    const controller = new AbortController();
    lookupStorybookGift(code, controller.signal)
      .then((g) => setGift(g))
      .catch(() => {});
    return () => controller.abort();
  }, [code, tries]);
  useEffect(() => {
    if (!gift || gift.status !== 'pending' || tries >= 20) return undefined;
    const timer = window.setTimeout(() => setTries((n) => n + 1), 3000);
    return () => window.clearTimeout(timer);
  }, [gift, tries]);

  useEffect(() => {
    trackEvent('storybook_gift_checkout_returned', { has_code: Boolean(code) });
  }, [code]);

  const link = code ? storybookGiftLink(code) : '';
  const paid = gift && gift.status !== 'pending';
  const share = gift ? t('storybookGift.sent.shareText', { from: gift.fromName, link }) : link;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      /* the link stays visible to copy by hand */
    }
  };

  return (
    <div className="glass-card p-6 text-center sm:p-10" data-testid="storybook-gift-sent">
      <p className="text-5xl" aria-hidden="true">🎁</p>
      <h2 className="mt-4 text-3xl font-semibold">{t(paid || !gift ? 'storybookGift.sent.title' : 'storybookGift.sent.pendingTitle')}</h2>
      <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
        {gift
          ? t(paid ? 'storybookGift.sent.body' : 'storybookGift.sent.pendingBody', { name: gift.toName, email: gift.toEmail })
          : t('storybookGift.sent.bodyNoCode')}
      </p>
      {gift && !paid && <Loader2 className="mx-auto mt-4 h-5 w-5 animate-spin text-primary" aria-label={t('storybookGift.sent.pendingTitle')} />}
      {link && (
        <div className="mx-auto mt-8 max-w-lg rounded-2xl bg-muted p-4 text-left">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
            {t('storybookGift.sent.linkTitle')}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t('storybookGift.sent.linkHelp')}</p>
          <p className="mt-3 break-all rounded-xl bg-card px-3 py-2 font-mono text-xs" data-testid="gift-link">{link}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Button type="button" variant="outline" className="min-h-[44px] rounded-full bg-card" onClick={copy}>
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? t('storybookGift.sent.copied') : t('storybookGift.sent.copy')}
            </Button>
            <Button asChild className="min-h-[44px] rounded-full">
              <a href={`https://wa.me/?text=${encodeURIComponent(share)}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle aria-hidden="true" />
                {t('storybookGift.sent.whatsapp')}
              </a>
            </Button>
          </div>
        </div>
      )}
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <Button asChild variant="outline" className="min-h-[44px] rounded-full bg-card">
          <Link to="/storybook/gift">{t('storybookGift.sent.another')}</Link>
        </Button>
        <Button asChild variant="ghost" className="min-h-[44px] rounded-full">
          <Link to="/storybook">{t('storybookGift.sent.seeBooks')}</Link>
        </Button>
      </div>
    </div>
  );
}
